import { useEffect, useRef, useState, type FormEvent } from "react";
import { Alert, Button, Spinner } from "@heroui/react";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import { BrandMark } from "../shell/BrandMark";
import { signIn, useAuth } from "@/lib/auth";

const FIELD =
  "h-11 w-full rounded-xl border border-border bg-surface-secondary px-3.5 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted focus-visible:border-foreground/40 focus-visible:ring-2 focus-visible:ring-focus";

/**
 * The sign-in page: one user, whose name and password are set in the
 * backend's .env. Shown full screen (no rail, no tabs) until signed in.
 */
export function LoginPage() {
  const { status, notice } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const notConfigured = status === "not-configured";

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy || notConfigured) return;
    // Read the form itself: a browser's saved-password autofill fills the
    // fields without telling the page until the first tap, so the state can lag.
    const form = new FormData(e.currentTarget);
    const name = String(form.get("username") ?? username).trim();
    const pass = String(form.get("password") ?? password);
    if (!name || !pass) {
      setError("Enter your username and password.");
      return;
    }
    setBusy(true);
    setError(null);
    const err = await signIn(name, pass);
    setBusy(false);
    if (err) {
      setError(err);
      setPassword("");
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <BrandMark className="scale-125" />
          <p className="text-sm text-muted">Your day-trading desk</p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-surface p-6 shadow-sm" noValidate>
          <div className="flex items-center gap-2">
            <LockKeyhole className="size-4 text-muted" aria-hidden />
            <h1 className="text-lg font-semibold text-foreground">Sign in</h1>
          </div>

          {notConfigured ? (
            <Alert status="warning">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Sign-in isn't set up yet</Alert.Title>
                <Alert.Description>
                  Add <code className="num">AUTH_USERNAME</code> and <code className="num">AUTH_PASSWORD</code> to <code className="num">backend/.env</code>, then restart the backend.
                </Alert.Description>
              </Alert.Content>
            </Alert>
          ) : (
            (error || notice) && (
              <Alert status={error ? "danger" : "default"}>
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>{error ?? notice}</Alert.Description>
                </Alert.Content>
              </Alert>
            )
          )}

          <div className="space-y-1.5">
            <label htmlFor="login-username" className="text-[13px] font-medium text-foreground">Username</label>
            <input
              ref={nameRef}
              id="login-username"
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={username}
              onChange={e => setUsername(e.target.value)}
              disabled={notConfigured}
              className={FIELD}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="login-password" className="text-[13px] font-medium text-foreground">Password</label>
            <div className="relative">
              <input
                id="login-password"
                name="password"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={notConfigured}
                className={`${FIELD} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShow(v => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                aria-pressed={show}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus"
              >
                {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" variant="primary" fullWidth isDisabled={notConfigured || busy}>
            {busy ? <Spinner size="sm" /> : "Sign in"}
          </Button>

          <p className="text-center text-xs leading-relaxed text-muted">
            You stay signed in on this device for a year, until you sign out.
          </p>
        </form>
      </div>
    </main>
  );
}
